import PropTypes from 'prop-types';
import { Box, Typography, Tooltip } from '@mui/material';
import { Iconify } from 'src/components';

export default function LostLeadsInsights({ kpis, byReason, byTreatment }) {
    const insights = [];

    if (kpis.totalLost > 0) {
        insights.push(`You lost ${kpis.totalLost} leads in this period, with an estimated revenue impact of \u00A3${kpis.lostRevenue?.toLocaleString() || 0}.`);
    }

    if (byReason.length > 0) {
        const topReason = byReason[0];
        const topReasonPct = kpis.totalLost > 0 ? ((topReason.count / kpis.totalLost) * 100).toFixed(0) : 0;
        insights.push(`"${topReason.label}" is your top loss reason at ${topReasonPct}%. Consider targeted strategies to address this.`);
    }

    if (byTreatment.length > 0) {
        const topTreatment = byTreatment[0];
        insights.push(`${topTreatment.name} has the most lost leads (${topTreatment.count}). Review pricing and consultation approach for this treatment.`);
    }

    if (byReason.length > 1) {
        const pricingRelated = byReason.find((r) => r.reason === 'TOO_EXPENSIVE');
        if (pricingRelated && kpis.totalLost > 0) {
            const pricePct = ((pricingRelated.count / kpis.totalLost) * 100).toFixed(0);
            insights.push(`${pricePct}% of leads cite cost as a factor. Consider offering payment plans or flexible pricing.`);
        }
    }

    // Avg days to archive insight
    if (kpis.avgDaysToArchive > 0) {
        if (kpis.avgDaysToArchive <= 7) {
            insights.push(`Leads are being archived quickly (avg ${kpis.avgDaysToArchive} days). Ensure enough follow-up attempts before archiving.`);
        } else if (kpis.avgDaysToArchive > 30) {
            insights.push(`Leads take an average of ${kpis.avgDaysToArchive} days before being archived. Consider setting follow-up reminders to engage sooner.`);
        }
    }

    // Archive rate insight
    if (kpis.archiveRate > 0) {
        // The rate divides leads archived in the period by leads CREATED in the period (different
        // cohorts), so it is an indicator, not a true loss rate: keep the wording neutral.
        if (kpis.archiveRate > 20) {
            insights.push(`Archived leads equal ${kpis.archiveRate}% of new leads this period (these are different groups of leads, so treat it as a guide). Consider reviewing your follow-up process.`);
        } else if (kpis.archiveRate < 5) {
            insights.push(`Archived leads equal only ${kpis.archiveRate}% of new leads this period (different groups of leads, so a guide only).`);
        }
    }

    // MoM change insight
    if (kpis.momChange && kpis.momChange.direction === 'up' && kpis.momChange.count > 0) {
        if (kpis.momChange.pct === 'New') {
            insights.push('No lost leads were recorded in the previous period, so any lost leads now are an increase. Investigate recent changes in your process.');
        } else {
            insights.push(`Lost leads increased by ${kpis.momChange.pct} compared to the previous period. Investigate recent changes in your process.`);
        }
    } else if (kpis.momChange && kpis.momChange.direction === 'down') {
        insights.push(`Lost leads decreased by ${kpis.momChange.pct.replace('-', '')} vs the previous period. Your retention efforts are paying off.`);
    }

    // Contact-related insight
    const contactReasons = byReason.filter((r) => ['NO_RESPONSE', 'UNABLE_TO_CONTACT', 'AWAITING_RESPONSE'].includes(r.reason));
    if (contactReasons.length > 0 && kpis.totalLost > 0) {
        const contactTotal = contactReasons.reduce((sum, r) => sum + r.count, 0);
        const contactPct = ((contactTotal / kpis.totalLost) * 100).toFixed(0);
        if (parseFloat(contactPct) > 20) {
            insights.push(`${contactPct}% of losses are contact-related. Consider adding WhatsApp or SMS follow-ups to improve reachability.`);
        }
    }

    if (insights.length === 0) {
        insights.push('Start collecting data to see insights about lost leads and how to reduce attrition.');
    }

    return (
        <Box
            sx={{
                background: 'linear-gradient(135deg, #dc2626 0%, #991b1b 100%)',
                borderRadius: '16px',
                p: '24px',
                color: '#fff',
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Iconify icon="mdi:lightbulb-on" width={20} height={20} />
                <Tooltip title="Auto-generated recommendations to help reduce lead loss." placement="bottom" arrow
                    slotProps={{ tooltip: { sx: { bgcolor: '#1e293b', fontSize: '12px', maxWidth: 280 } } }}>
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, cursor: 'help' }}>
                        <Typography sx={{ fontSize: '16px', fontWeight: 600 }}>
                            Loss Prevention Insights
                        </Typography>
                        <Iconify icon="mdi:information-outline" width={14} sx={{ color: 'rgba(255,255,255,0.6)' }} />
                    </Box>
                </Tooltip>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {insights.map((insight, index) => (
                    <Box
                        key={index}
                        sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 1.5,
                            p: 1.5,
                            bgcolor: 'rgba(255,255,255,0.1)',
                            borderRadius: '10px',
                        }}
                    >
                        <Typography sx={{ fontSize: '14px', lineHeight: 1.5 }}>
                            {insight}
                        </Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    );
}

LostLeadsInsights.propTypes = {
    kpis: PropTypes.object,
    byReason: PropTypes.array,
    byTreatment: PropTypes.array,
};

LostLeadsInsights.defaultProps = {
    kpis: {},
    byReason: [],
    byTreatment: [],
};
